<template>
  <HrPageLayout title="HR Meetings" subtitle="Schedule and manage HR meetings" icon="ðŸ“…">

    <div class="mtg-page">
      <!-- ”€”€ Toolbar ”€”€ -->
      <div class="mtg-toolbar">
        <div class="mtg-tabs">
          <button class="mtg-tab" :class="{ 'mtg-tab-active': activeView === 'list' }" @click="activeView = 'list'">
            <i class="bx bx-list-ul"></i><span>All Meetings</span>
            <span v-if="pagination.totalMeetings" class="mtg-tab-count">{{ pagination.totalMeetings }}</span>
          </button>
          <button class="mtg-tab" :class="{ 'mtg-tab-active': activeView === 'upcoming' }" @click="activeView = 'upcoming'">
            <i class="bx bx-time-five"></i><span>Upcoming</span>
            <span v-if="metrics.upcomingMeetings" class="mtg-tab-count mtg-tab-count-green">{{ metrics.upcomingMeetings }}</span>
          </button>
        </div>
        <div class="mtg-toolbar-right">
          <button class="mtg-btn mtg-btn-ghost" @click="refreshData"><i class="bx bx-refresh"></i><span>Refresh</span></button>
          <button class="mtg-btn mtg-btn-primary" @click="createMeeting"><i class="bx bx-plus"></i><span>Schedule Meeting</span></button>
        </div>
      </div>

      <!-- ”€”€ Summary Cards ”€”€ -->
      <div class="mtg-summary-row">
        <div class="mtg-summary-card" style="--c:#3b82f6">
          <div class="mtg-summary-icon" style="background:#eff6ff;color:#3b82f6"><i class="bx bx-calendar-check"></i></div>
          <div><span class="mtg-summary-label">Total Meetings</span><span class="mtg-summary-value">{{ metrics.totalMeetings || 0 }}</span><span class="mtg-summary-sub">Past 30 days</span></div>
        </div>
        <div class="mtg-summary-card" style="--c:#059669">
          <div class="mtg-summary-icon" style="background:#f0fdf4;color:#059669"><i class="bx bx-calendar-event"></i></div>
          <div><span class="mtg-summary-label">Upcoming</span><span class="mtg-summary-value">{{ metrics.upcomingMeetings || 0 }}</span><span class="mtg-summary-sub">Next 7 days</span></div>
        </div>
        <div class="mtg-summary-card" style="--c:#0ea5e9">
          <div class="mtg-summary-icon" style="background:#f0f9ff;color:#0ea5e9"><i class="bx bx-calendar-alt"></i></div>
          <div><span class="mtg-summary-label">This Month</span><span class="mtg-summary-value">{{ metrics.meetingsThisMonth || 0 }}</span><span class="mtg-summary-sub">{{ currentMonthName }}</span></div>
        </div>
        <div class="mtg-summary-card" style="--c:#f59e0b">
          <div class="mtg-summary-icon" style="background:#fffbeb;color:#f59e0b"><i class="bx bx-calendar-star"></i></div>
          <div><span class="mtg-summary-label">Today</span><span class="mtg-summary-value">{{ metrics.todayMeetings || 0 }}</span><span class="mtg-summary-sub">{{ today }}</span></div>
        </div>
      </div>

      <!-- ”€”€ Filters ”€”€ -->
      <div class="mtg-card mtg-filters-card">
        <div class="mtg-filters-row">
          <div class="mtg-filter-group">
            <label>Type</label>
            <select v-model="filters.meetingType" class="mtg-select">
              <option value="">All Types</option>
              <option v-for="type in meetingTypes" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
          <div class="mtg-filter-group">
            <label>Department</label>
            <select v-model="filters.departmentId" class="mtg-select">
              <option value="">All Departments</option>
              <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
            </select>
          </div>
          <div class="mtg-filter-group">
            <label>From</label>
            <input v-model="filters.startDate" type="date" class="mtg-input">
          </div>
          <div class="mtg-filter-group">
            <label>To</label>
            <input v-model="filters.endDate" type="date" class="mtg-input">
          </div>
          <div class="mtg-filter-group mtg-filter-search">
            <label>Search</label>
            <div class="mtg-search-wrap">
              <i class="bx bx-search"></i>
              <input v-model="filters.search" class="mtg-input" placeholder="Search meetings...">
            </div>
          </div>
          <button class="mtg-btn mtg-btn-ghost mtg-filter-reset" @click="resetFilters"><i class="bx bx-x"></i> Reset</button>
        </div>
      </div>

      <!-- ”€”€ Main Layout: List + Upcoming sidebar ”€”€ -->
      <div class="mtg-main-layout">
        <!-- Meetings list -->
        <div class="mtg-card mtg-list-card">
          <div class="mtg-card-header">
            <span class="mtg-card-title"><i class="bx bx-list-ul"></i> Meetings List</span>
            <span class="mtg-row-count">{{ pagination.totalMeetings }} meeting{{ pagination.totalMeetings !== 1 ? 's' : '' }}</span>
          </div>
          <div v-if="loading" class="mtg-loading"><div class="mtg-spinner"></div><span>Loading meetings...</span></div>
          <div v-else-if="!meetings.length" class="mtg-empty">
            <i class="bx bx-calendar-x"></i><h5>No meetings found</h5>
            <p>Try adjusting your filters or schedule a new meeting.</p>
            <button class="mtg-btn mtg-btn-primary" @click="createMeeting"><i class="bx bx-plus"></i> Schedule Meeting</button>
          </div>
          <div v-else class="mtg-table-wrap">
            <table class="mtg-table">
              <thead>
                <tr><th>#</th><th>Meeting</th><th>Type</th><th>Date & Time</th><th>Location</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="(m, idx) in meetings" :key="m.id" :class="{ 'mtg-row-today': isMeetingToday(m.startDate) }">
                  <td class="mtg-td-num">{{ idx + 1 }}</td>
                  <td>
                    <div class="mtg-meeting-cell">
                      <div class="mtg-meeting-avatar" :class="getMeetingTypeClass(m.meetingType)">
                        <i class="bx bx-calendar-event"></i>
                      </div>
                      <div>
                        <span class="mtg-meeting-title">{{ m.title }}</span>
                        <span class="mtg-meeting-code">{{ m.meetingCode }}</span>
                      </div>
                    </div>
                  </td>
                  <td><span class="mtg-type-pill" :class="getMeetingTypeClass(m.meetingType)">{{ m.meetingType }}</span></td>
                  <td>
                    <span class="mtg-date">{{ formatDate(m.startDate) }}</span>
                    <span v-if="m.startTime" class="mtg-time"><i class="bx bx-time"></i> {{ formatTime(m.startTime) }} €“ {{ formatTime(m.endTime) }}</span>
                  </td>
                  <td>
                    <span v-if="m.location" class="mtg-location"><i class="bx bx-map-pin"></i> {{ m.location }}</span>
                    <span v-else-if="m.virtualMeetingLink" class="mtg-virtual"><i class="bx bx-video"></i> Virtual</span>
                    <span v-else class="mtg-na">€”</span>
                  </td>
                  <td><span class="mtg-status-pill" :class="getStatusClass(m.status)">{{ m.status }}</span></td>
                  <td>
                    <div class="mtg-row-actions">
                      <button class="mtg-action-btn mtg-action-view" @click="viewMeetingDetails(m)" title="View"><i class="bx bx-show"></i></button>
                      <button class="mtg-action-btn mtg-action-edit" @click="editMeeting(m)" title="Edit"><i class="bx bx-edit"></i></button>
                      <button v-if="m.status !== 'Canceled'" class="mtg-action-btn mtg-action-cancel" @click="cancelMeeting(m)" title="Cancel"><i class="bx bx-x-circle"></i></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Pagination -->
          <div v-if="meetings.length && pagination.totalPages > 1" class="mtg-pagination">
            <span class="mtg-pagination-info">Showing {{ meetings.length }} of {{ pagination.totalMeetings }}</span>
            <nav class="mtg-pages">
              <button class="mtg-page-btn" :disabled="pagination.currentPage <= 1" @click="changePage(pagination.currentPage - 1)"><i class="bx bx-chevron-left"></i></button>
              <button v-for="page in getPageNumbers()" :key="page" class="mtg-page-btn" :class="{ 'mtg-page-active': page === pagination.currentPage, 'mtg-page-dots': page === '...' }" @click="page !== '...' && changePage(page)">{{ page }}</button>
              <button class="mtg-page-btn" :disabled="pagination.currentPage >= pagination.totalPages" @click="changePage(pagination.currentPage + 1)"><i class="bx bx-chevron-right"></i></button>
            </nav>
          </div>
        </div>

        <!-- Upcoming sidebar -->
        <div class="mtg-sidebar">
          <div class="mtg-card">
            <div class="mtg-card-header"><span class="mtg-card-title"><i class="bx bx-time-five"></i> Upcoming</span></div>
            <div v-if="!upcomingMeetings.length" class="mtg-empty mtg-empty-sm"><i class="bx bx-calendar-check"></i><p>No upcoming meetings</p></div>
            <div v-else class="mtg-upcoming-list">
              <div v-for="m in upcomingMeetings" :key="m.id" class="mtg-upcoming-item" @click="viewMeetingDetails(m)">
                <div class="mtg-upcoming-date">
                  <span class="mtg-upcoming-month">{{ formatDateMonth(m.startDate) }}</span>
                  <span class="mtg-upcoming-day">{{ formatDateDay(m.startDate) }}</span>
                </div>
                <div class="mtg-upcoming-info">
                  <span class="mtg-upcoming-title">{{ m.title }}</span>
                  <span class="mtg-upcoming-meta"><i class="bx bx-time"></i> {{ formatMeetingTime(m) }}</span>
                  <span v-if="m.location" class="mtg-upcoming-meta"><i class="bx bx-map-pin"></i> {{ m.location }}</span>
                </div>
                <span class="mtg-type-pill mtg-type-pill-sm" :class="getMeetingTypeClass(m.meetingType)">{{ m.meetingType }}</span>
              </div>
            </div>
          </div>

          <!-- Type breakdown -->
          <div class="mtg-card" v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length">
            <div class="mtg-card-header"><span class="mtg-card-title"><i class="bx bx-bar-chart-alt-2"></i> By Type</span></div>
            <div class="mtg-type-breakdown">
              <div v-for="(count, type) in metrics.typeCounts" :key="type" class="mtg-type-row">
                <div class="mtg-type-row-header">
                  <span class="mtg-type-name">{{ type }}</span>
                  <span class="mtg-type-count">{{ count }}</span>
                </div>
                <div class="mtg-type-bar-wrap">
                  <div class="mtg-type-bar" :class="getMeetingTypeClass(type)" :style="{ width: getMeetingTypePercentage(type) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SLIDE-IN PANELS -->
    <Teleport to="body">

      <!-- Schedule / Edit Meeting Panel -->
      <div class="mtg-overlay" v-if="showMeetingForm" @click.self="closeMeetingModal"></div>
      <div class="mtg-panel" :class="{ 'mtg-panel-open': showMeetingForm }">
        <div class="mtg-panel-header">
          <div class="mtg-panel-title-wrap">
            <div class="mtg-panel-icon"><i class="bx" :class="isEditMode ? 'bx-edit' : 'bx-calendar-plus'"></i></div>
            <div>
              <h4 class="mtg-panel-title">{{ isEditMode ? 'Edit Meeting' : 'Schedule Meeting' }}</h4>
              <p class="mtg-panel-subtitle">{{ isEditMode ? 'Update meeting details' : 'Fill in the details below' }}</p>
            </div>
          </div>
          <button class="mtg-panel-close" @click="closeMeetingModal"><i class="bx bx-x"></i></button>
        </div>
        <div class="mtg-panel-body">
          <form @submit.prevent="submitMeeting">
            <div class="mtg-form-section">
              <div class="mtg-form-section-label"><i class="bx bx-info-circle"></i> Basic Details</div>
              <div class="mtg-form-grid">
                <div class="mtg-field mtg-field-full">
                  <label class="mtg-label">Title <span class="mtg-req">*</span></label>
                  <input v-model="meetingForm.title" class="mtg-input" placeholder="Meeting title..." required>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">Type <span class="mtg-req">*</span></label>
                  <select v-model="meetingForm.meetingType" class="mtg-select" required>
                    <option value="" disabled>Select type...</option>
                    <option v-for="t in meetingTypes" :key="t" :value="t">{{ t }}</option>
                  </select>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">Department</label>
                  <select v-model="meetingForm.departmentId" class="mtg-select">
                    <option value="">All Departments</option>
                    <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.departmentName }}</option>
                  </select>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">Date <span class="mtg-req">*</span></label>
                  <div class="mtg-input-icon-wrap"><i class="bx bx-calendar mtg-input-icon"></i>
                    <input v-model="meetingForm.startDate" type="date" class="mtg-input mtg-input-icon-pad" required>
                  </div>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">Start Time <span class="mtg-req">*</span></label>
                  <div class="mtg-input-icon-wrap"><i class="bx bx-time mtg-input-icon"></i>
                    <input v-model="meetingForm.startTime" type="time" class="mtg-input mtg-input-icon-pad" required>
                  </div>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">Duration (min) <span class="mtg-req">*</span></label>
                  <input v-model="meetingForm.duration" type="number" min="15" step="15" class="mtg-input" required>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">End Time</label>
                  <input v-model="meetingForm.endTime" type="time" class="mtg-input" disabled>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">Priority</label>
                  <div class="mtg-priority-group">
                    <label class="mtg-priority-opt" :class="{ 'mtg-priority-low': meetingForm.priority === 'Low' }"><input type="radio" v-model="meetingForm.priority" value="Low" hidden> Low</label>
                    <label class="mtg-priority-opt" :class="{ 'mtg-priority-medium': meetingForm.priority === 'Medium' }"><input type="radio" v-model="meetingForm.priority" value="Medium" hidden> Medium</label>
                    <label class="mtg-priority-opt" :class="{ 'mtg-priority-high': meetingForm.priority === 'High' }"><input type="radio" v-model="meetingForm.priority" value="High" hidden> High</label>
                  </div>
                </div>
                <div class="mtg-field">
                  <label class="mtg-label">Status</label>
                  <select v-model="meetingForm.status" class="mtg-select">
                    <option value="Scheduled">Scheduled</option>
                    <option value="Postponed">Postponed</option>
                    <option value="Canceled">Canceled</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="mtg-form-section">
              <div class="mtg-form-section-label"><i class="bx bx-map-pin"></i> Location</div>
              <div class="mtg-form-grid">
                <div class="mtg-field mtg-field-full">
                  <label class="mtg-label">Physical Location</label>
                  <div class="mtg-input-icon-wrap"><i class="bx bx-map-pin mtg-input-icon"></i>
                    <input v-model="meetingForm.location" class="mtg-input mtg-input-icon-pad" placeholder="Room, building...">
                  </div>
                </div>
                <div class="mtg-field mtg-field-full">
                  <label class="mtg-label">Virtual Meeting Link</label>
                  <div class="mtg-input-icon-wrap"><i class="bx bx-video mtg-input-icon"></i>
                    <input v-model="meetingForm.virtualMeetingLink" class="mtg-input mtg-input-icon-pad" placeholder="https://...">
                  </div>
                </div>
              </div>
            </div>
            <div class="mtg-form-section">
              <div class="mtg-form-section-label"><i class="bx bx-file-blank"></i> Agenda</div>
              <div style="padding:14px 20px">
                <textarea v-model="meetingForm.agenda" class="mtg-textarea" rows="4" placeholder="Meeting agenda..."></textarea>
              </div>
            </div>
            <div class="mtg-form-section">
              <div class="mtg-form-section-label"><i class="bx bx-group"></i> Participants</div>
              <div style="padding:14px 20px">
                <div class="mtg-scope-info"><i class="bx bx-info-circle"></i> {{ scopedEmployeesSummary }}</div>
                <div v-if="!meetingForm.departmentId" style="margin-top:12px">
                  <div class="mtg-form-grid">
                    <div class="mtg-field mtg-field-full">
                      <label class="mtg-label">Participant Mode</label>
                      <div class="mtg-mode-group">
                        <label class="mtg-mode-opt" :class="{ 'mtg-mode-active': meetingForm.participantMode === 'all' }"><input type="radio" v-model="meetingForm.participantMode" value="all" hidden><i class="bx bx-world"></i> All Employees</label>
                        <label class="mtg-mode-opt" :class="{ 'mtg-mode-active': meetingForm.participantMode === 'specific' }"><input type="radio" v-model="meetingForm.participantMode" value="specific" hidden><i class="bx bx-user-check"></i> Specific</label>
                      </div>
                    </div>
                    <div v-if="meetingForm.participantMode === 'specific'" class="mtg-field mtg-field-full">
                      <label class="mtg-label">Search Employees</label>
                      <div class="mtg-input-icon-wrap"><i class="bx bx-search mtg-input-icon"></i>
                        <input v-model="participantSearch" class="mtg-input mtg-input-icon-pad" placeholder="Search by name or department...">
                      </div>
                    </div>
                    <div v-if="meetingForm.participantMode === 'specific'" class="mtg-field mtg-field-full">
                      <label class="mtg-label">Select Participants</label>
                      <select v-model="meetingForm.participants" class="mtg-select" multiple style="height:120px">
                        <option v-for="e in participantOptions" :key="e.id" :value="e.id">{{ e.firstName }} {{ e.lastName }} ({{ e.departmentName || 'No Dept' }})</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mtg-form-section">
              <div class="mtg-form-section-label"><i class="bx bx-paperclip"></i> Attachments</div>
              <div style="padding:14px 20px">
                <label class="mtg-file-drop">
                  <input type="file" multiple accept=".pdf,.doc,.docx" @change="onMeetingAttachmentsChange" hidden>
                  <i class="bx bx-cloud-upload mtg-file-icon"></i>
                  <span>Click to upload files</span>
                  <span class="mtg-file-hint">PDF, DOC, DOCX</span>
                </label>
              </div>
            </div>
            <div class="mtg-panel-footer">
              <button type="button" class="mtg-btn mtg-btn-ghost" @click="closeMeetingModal"><i class="bx bx-x"></i> Cancel</button>
              <button type="submit" class="mtg-btn mtg-btn-primary" :disabled="submitLoading">
                <span v-if="submitLoading" class="mtg-btn-spinner"></span>
                <i v-else class="bx" :class="isEditMode ? 'bx-save' : 'bx-calendar-check'"></i>
                {{ submitLoading ? 'Saving...' : (isEditMode ? 'Update Meeting' : 'Schedule Meeting') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="mtg-overlay" v-if="showMeetingDetails" @click.self="closeMeetingDetailsModal"></div>
      <div class="mtg-panel" :class="{ 'mtg-panel-open': showMeetingDetails }">
        <div class="mtg-panel-header" v-if="selectedMeeting" style="background:linear-gradient(135deg,#1e293b,#334155)">
          <div class="mtg-panel-title-wrap">
            <div class="mtg-panel-icon"><i class="bx bx-calendar-event"></i></div>
            <div>
              <h4 class="mtg-panel-title">{{ selectedMeeting.title }}</h4>
              <p class="mtg-panel-subtitle">{{ formatDate(selectedMeeting.startDate) }}</p>
            </div>
          </div>
          <button class="mtg-panel-close" @click="closeMeetingDetailsModal"><i class="bx bx-x"></i></button>
        </div>
        <div class="mtg-panel-body" v-if="selectedMeeting">
          <div class="mtg-detail-badges">
            <span class="mtg-type-pill" :class="getMeetingTypeClass(selectedMeeting.meetingType)">{{ selectedMeeting.meetingType }}</span>
            <span class="mtg-status-pill" :class="getStatusClass(selectedMeeting.status)">{{ selectedMeeting.status }}</span>
            <span class="mtg-priority-pill" :class="'mtg-priority-' + (selectedMeeting.priority || 'medium').toLowerCase()">{{ selectedMeeting.priority }}</span>
          </div>
          <div class="mtg-detail-meta">
            <div class="mtg-detail-meta-item"><i class="bx bx-calendar"></i><div><span class="mtg-meta-label">Date</span><span class="mtg-meta-value">{{ formatDate(selectedMeeting.startDate) }}</span></div></div>
            <div class="mtg-detail-meta-item" v-if="selectedMeeting.startTime"><i class="bx bx-time"></i><div><span class="mtg-meta-label">Time</span><span class="mtg-meta-value">{{ formatTime(selectedMeeting.startTime) }} €“ {{ formatTime(selectedMeeting.endTime) }}</span></div></div>
            <div class="mtg-detail-meta-item"><i class="bx bx-map-pin"></i><div><span class="mtg-meta-label">Location</span><span class="mtg-meta-value">{{ selectedMeeting.location || 'N/A' }}</span></div></div>
            <div class="mtg-detail-meta-item"><i class="bx bx-user"></i><div><span class="mtg-meta-label">Organizer</span><span class="mtg-meta-value">{{ selectedMeeting.organizer || 'N/A' }}</span></div></div>
          </div>
          <div v-if="selectedMeeting.virtualMeetingLink" class="mtg-form-section">
            <div class="mtg-form-section-label"><i class="bx bx-video"></i> Virtual Meeting</div>
            <div style="padding:14px 20px">
              <a :href="selectedMeeting.virtualMeetingLink" target="_blank" class="mtg-join-btn"><i class="bx bx-video"></i> Join Meeting</a>
            </div>
          </div>
          <div class="mtg-form-section" v-if="selectedMeeting.agenda">
            <div class="mtg-form-section-label"><i class="bx bx-file-blank"></i> Agenda</div>
            <p class="mtg-detail-text">{{ selectedMeeting.agenda }}</p>
          </div>
          <div class="mtg-form-section" v-if="selectedMeeting.participantsList && selectedMeeting.participantsList.length">
            <div class="mtg-form-section-label"><i class="bx bx-group"></i> Participants <span class="mtg-count-badge">{{ selectedMeeting.participantsList.length }}</span></div>
            <div class="mtg-participants-list">
              <div v-for="(p, i) in selectedMeeting.participantsList" :key="i" class="mtg-participant-item">
                <div class="mtg-participant-avatar">{{ getInitials(p.name) }}</div>
                <div class="mtg-participant-info">
                  <span class="mtg-participant-name">{{ p.name }}</span>
                  <span class="mtg-participant-dept">{{ p.department || 'N/A' }}</span>
                </div>
                <span class="mtg-response-pill" :class="getResponseClass(p.response)">{{ p.response || 'Pending' }}</span>
              </div>
            </div>
          </div>
          <div class="mtg-form-section" v-if="selectedMeeting.minutes">
            <div class="mtg-form-section-label"><i class="bx bx-file-find"></i> Meeting Minutes</div>
            <p class="mtg-detail-text">{{ selectedMeeting.minutes.content }}</p>
            <div v-if="selectedMeeting.minutes.actionItems && selectedMeeting.minutes.actionItems.length" style="padding:0 20px 16px">
              <table class="mtg-table">
                <thead><tr><th>Action Item</th><th>Assigned To</th><th>Due Date</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="(item, i) in selectedMeeting.minutes.actionItems" :key="i">
                    <td>{{ item.description }}</td><td>{{ item.assignedTo }}</td>
                    <td>{{ formatDate(item.dueDate) }}</td>
                    <td><span class="mtg-status-pill" :class="getActionItemClass(item.status || 'Pending')">{{ item.status || 'Pending' }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="mtg-panel-footer">
            <button class="mtg-btn mtg-btn-ghost" @click="closeMeetingDetailsModal"><i class="bx bx-x"></i> Close</button>
            <div style="display:flex;gap:8px">
              <button v-if="selectedMeeting.status === 'Scheduled' || selectedMeeting.status === 'Completed'" class="mtg-btn mtg-btn-outline-info" @click="openMeetingMinutesModal(selectedMeeting)">
                <i class="bx bx-file-blank"></i> {{ selectedMeeting.minutes ? 'Edit Minutes' : 'Add Minutes' }}
              </button>
              <button v-if="selectedMeeting.status === 'Scheduled'" class="mtg-btn mtg-btn-outline-success" @click="completeMeeting(selectedMeeting)">
                <i class="bx bx-check-circle"></i> Complete
              </button>
              <button class="mtg-btn mtg-btn-primary" @click="editMeeting(selectedMeeting)"><i class="bx bx-edit"></i> Edit</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Cancel Meeting Panel -->
      <div class="mtg-overlay" v-if="showCancelForm" @click.self="showCancelForm = false"></div>
      <div class="mtg-panel mtg-panel-sm" :class="{ 'mtg-panel-open': showCancelForm }">
        <div class="mtg-panel-header" style="background:linear-gradient(135deg,#ef4444,#dc2626)">
          <div class="mtg-panel-title-wrap">
            <div class="mtg-panel-icon"><i class="bx bx-x-circle"></i></div>
            <div><h4 class="mtg-panel-title">Cancel Meeting</h4><p class="mtg-panel-subtitle">This action cannot be undone</p></div>
          </div>
          <button class="mtg-panel-close" @click="showCancelForm = false"><i class="bx bx-x"></i></button>
        </div>
        <div class="mtg-panel-body">
          <div class="mtg-form-section">
            <div class="mtg-form-section-label"><i class="bx bx-info-circle"></i> Cancellation Details</div>
            <div style="padding:16px 20px;display:flex;flex-direction:column;gap:14px">
              <div class="mtg-field">
                <label class="mtg-label">Reason for cancellation</label>
                <textarea v-model="cancellationReason" class="mtg-textarea" rows="4" placeholder="Please provide a reason..."></textarea>
              </div>
              <label class="mtg-toggle-item" :class="{ 'mtg-toggle-on': notifyParticipants }">
                <div class="mtg-toggle-info"><i class="bx bx-bell"></i><div><span class="mtg-toggle-title">Notify Participants</span><span class="mtg-toggle-desc">Send cancellation email</span></div></div>
                <div class="mtg-switch" @click="notifyParticipants = !notifyParticipants"><div class="mtg-switch-knob"></div></div>
              </label>
            </div>
          </div>
          <div class="mtg-panel-footer">
            <button class="mtg-btn mtg-btn-ghost" @click="showCancelForm = false"><i class="bx bx-x"></i> Back</button>
            <button class="mtg-btn mtg-btn-danger" @click="confirmCancelMeeting"><i class="bx bx-x-circle"></i> Cancel Meeting</button>
          </div>
        </div>
      </div>

      <!-- Meeting Minutes Panel -->
      <div class="mtg-overlay" v-if="showMinutesForm" @click.self="showMinutesForm = false"></div>
      <div class="mtg-panel" :class="{ 'mtg-panel-open': showMinutesForm }">
        <div class="mtg-panel-header" style="background:linear-gradient(135deg,#0ea5e9,#0284c7)">
          <div class="mtg-panel-title-wrap">
            <div class="mtg-panel-icon"><i class="bx bx-file-blank"></i></div>
            <div><h4 class="mtg-panel-title">Meeting Minutes</h4><p class="mtg-panel-subtitle">Document what was discussed</p></div>
          </div>
          <button class="mtg-panel-close" @click="showMinutesForm = false"><i class="bx bx-x"></i></button>
        </div>
        <div class="mtg-panel-body">
          <form @submit.prevent="submitMeetingMinutes">
            <div class="mtg-form-section">
              <div class="mtg-form-section-label"><i class="bx bx-file-blank"></i> Minutes Content <span class="mtg-req">*</span></div>
              <div style="padding:14px 20px">
                <textarea v-model="minutesForm.content" class="mtg-textarea" rows="8" placeholder="Write meeting minutes here..." required></textarea>
              </div>
            </div>
            <div class="mtg-form-section">
              <div class="mtg-form-section-label">
                <span><i class="bx bx-list-check"></i> Action Items</span>
                <button type="button" class="mtg-btn mtg-btn-ghost mtg-btn-sm" @click="addActionItem"><i class="bx bx-plus"></i> Add</button>
              </div>
              <div style="padding:14px 20px;display:flex;flex-direction:column;gap:10px">
                <div v-for="(item, idx) in minutesForm.actionItems" :key="idx" class="mtg-action-item-row">
                  <input v-model="item.description" class="mtg-input" placeholder="Action item...">
                  <input v-model="item.assignedTo" class="mtg-input" placeholder="Assigned to...">
                  <input v-model="item.dueDate" type="date" class="mtg-input">
                  <button type="button" class="mtg-action-btn mtg-action-cancel" @click="removeActionItem(idx)"><i class="bx bx-trash"></i></button>
                </div>
                <p v-if="!minutesForm.actionItems.length" class="mtg-empty-text">No action items yet.</p>
              </div>
            </div>
            <div class="mtg-form-section">
              <div class="mtg-form-section-label"><i class="bx bx-cog"></i> Options</div>
              <div style="padding:14px 20px">
                <label class="mtg-toggle-item" :class="{ 'mtg-toggle-on': minutesForm.notifyParticipants }">
                  <div class="mtg-toggle-info"><i class="bx bx-send"></i><div><span class="mtg-toggle-title">Send to Participants</span><span class="mtg-toggle-desc">Email minutes to all participants</span></div></div>
                  <div class="mtg-switch" @click="minutesForm.notifyParticipants = !minutesForm.notifyParticipants"><div class="mtg-switch-knob"></div></div>
                </label>
              </div>
            </div>
            <div class="mtg-panel-footer">
              <button type="button" class="mtg-btn mtg-btn-ghost" @click="showMinutesForm = false"><i class="bx bx-x"></i> Cancel</button>
              <button type="submit" class="mtg-btn mtg-btn-primary"><i class="bx bx-save"></i> Save Minutes</button>
            </div>
          </form>
        </div>
      </div>

    </Teleport>

  </HrPageLayout>
</template>
<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import * as bootstrap from "bootstrap";

export default {
  name: "HRMeetings",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      meetings: [],
      upcomingMeetings: [],
      departments: [],
      employees: [],
      meetingTypes: ['Team Meeting', 'One-on-One', 'Board Meeting', 'Interview', 'Conference Call', 'Training', 'Workshop', 'Brainstorming', 'Review', 'Other'],
      filters: {
        departmentId: "",
        meetingType: "",
        startDate: this.getDateOffset(-30), // Last 30 days
        endDate: this.getDateOffset(30), // Next 30 days
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalMeetings: 0,
        limit: 10
      },
      metrics: {
        totalMeetings: 0,
        upcomingMeetings: 0,
        meetingsThisMonth: 0,
        todayMeetings: 0,
        typeCounts: {}
      },
      selectedMeeting: null,
      meetingForm: {
        title: "",
        agenda: "",
        meetingType: "",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        duration: 60,
        location: "",
        virtualMeetingLink: "",
        departmentId: "",
        participantMode: "all",
        participants: [],
        priority: "Medium",
        status: "Scheduled"
      },
      isEditMode: false,
      cancellationReason: "",
      notifyParticipants: true,
      minutesForm: {
        content: "",
        actionItems: [],
        notifyParticipants: true
      },
      participantSearch: "",
      // Panel visibility
      showMeetingForm: false,
      showMeetingDetails: false,
      showCancelForm: false,
      showMinutesForm: false,
      activeView: 'list',
    };
  },
  computed: {
    today() {
      return new Date().toLocaleDateString();
    },
    currentMonthName() {
      return new Date().toLocaleString('default', { month: 'long' });
    },
    scopedEmployees() {
      if (!this.meetingForm.departmentId) return this.employees;
      return this.employees.filter((employee) => this.getEmployeeDepartmentId(employee) === this.meetingForm.departmentId);
    },
    scopedEmployeesSummary() {
      if (!this.meetingForm.departmentId) {
        return `${this.employees.length} employee(s) in all departments`;
      }
      const selectedDepartment = this.departments.find((dept) => String(dept.id) === String(this.meetingForm.departmentId));
      const departmentLabel = selectedDepartment?.departmentName || "Selected department";
      return `${this.scopedEmployees.length} employee(s) in ${departmentLabel}`;
    },
    participantOptions() {
      // Start with scoped employees (filtered by department if selected)
      let filteredEmployees = this.scopedEmployees;
      
      // Apply search filter if there's a search query
      const query = (this.participantSearch || "").trim().toLowerCase();
      if (query) {
        filteredEmployees = filteredEmployees.filter((employee) => {
          const fullName = `${employee.firstName || ""} ${employee.lastName || ""}`.toLowerCase();
          const departmentName = (employee.departmentName || "").toLowerCase();
          const empNo = (employee.empNo || "").toLowerCase();
          return fullName.includes(query) || departmentName.includes(query) || empNo.includes(query);
        });
      }
      
      return filteredEmployees;
    }
  },
  mounted() {
    this.getDepartments();
    this.getEmployees();
    this.getMeetings();
  },
  beforeUnmount() {
    this.cleanupModalArtifacts();
  },
  methods: {
    cleanupModalArtifacts() {
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    },
    showModalById(modalId) {
      const el = document.getElementById(modalId);
      if (!el) return;
      try {
        this.cleanupModalArtifacts();
        const noBackdropIds = ["meetingModal", "meetingDetailsModal", "cancelMeetingModal", "meetingMinutesModal"];
        const options = noBackdropIds.includes(modalId) ? { backdrop: false, keyboard: true } : {};
        const existing = bootstrap.Modal.getInstance(el);
        if (existing) {
          existing.dispose();
        }
        const modal = new bootstrap.Modal(el, options);
        modal.show();
      } catch (_) {
        // Fallback: never leave page locked if Bootstrap modal lifecycle fails.
        el.classList.add("show");
        el.style.display = "block";
        el.setAttribute("aria-modal", "true");
        el.removeAttribute("aria-hidden");
      }
    },
    forceHideModalElement(el) {
      if (!el) return;
      el.classList.remove("show");
      el.style.display = "none";
      el.classList.remove("d-block");
      el.removeAttribute("aria-modal");
      el.setAttribute("aria-hidden", "true");
    },
    hideModalById(modalId) {
      const el = document.getElementById(modalId);
      if (!el) {
        this.cleanupModalArtifacts();
        return;
      }
      try {
        const modal = bootstrap.Modal.getInstance(el);
        if (modal) {
          const onHidden = () => {
            el.removeEventListener("hidden.bs.modal", onHidden);
            this.forceHideModalElement(el);
            this.cleanupModalArtifacts();
          };
          el.addEventListener("hidden.bs.modal", onHidden);
          modal.hide();
          // If Bootstrap does not emit hidden (edge cases / static backdrop quirks), still clear UI.
          setTimeout(() => {
            if (el.classList.contains("show")) {
              el.removeEventListener("hidden.bs.modal", onHidden);
              try {
                modal.dispose();
              } catch (_) {
                /* ignore */
              }
              this.forceHideModalElement(el);
            }
            this.cleanupModalArtifacts();
          }, 400);
          return;
        }
      } catch (_) {
        /* fall through */
      }
      this.forceHideModalElement(el);
      setTimeout(() => this.cleanupModalArtifacts(), 50);
    },
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");
      data.append("rows_per_page", "10000"); // Request all employees for meeting participants
      data.append("status", "Active"); // Only active employees
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    getMeetings() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getMeetings");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.meetingType) {
        data.append("meetingType", this.filters.meetingType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.meetings = res.data.data.meetings || [];
              this.metrics = res.data.data.metrics || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalMeetings: 0,
                limit: 10
              };

              // Get upcoming meetings (next 7 days)
              const today = new Date();
              const nextWeek = new Date();
              nextWeek.setDate(today.getDate() + 7);

              this.upcomingMeetings = this.meetings
                  .filter(meeting => {
                    const meetingDate = new Date(meeting.startDate);
                    return meetingDate >= today && meetingDate <= nextWeek && meeting.status !== 'Canceled';
                  })
                  .slice(0, 5); // Top 5 upcoming meetings
            } else {
              console.error("Error fetching meetings:", res.data.message);
              this.showToast("error", "Failed to load meetings data: " + res.data.message);
              this.meetings = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching meetings");
            this.meetings = [];
            this.metrics = {};
          });
    },


    refreshData() {
      this.getMeetings();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        meetingType: "",
        startDate: this.getDateOffset(-30),
        endDate: this.getDateOffset(30),
        search: "",
        page: 1,
        limit: 10
      };
      this.getMeetings();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getMeetings();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    createMeeting() {
      this.isEditMode = false;
      this.meetingForm = {
        title: "", agenda: "", meetingType: "",
        startDate: new Date().toISOString().split('T')[0],
        duration: 60, startTime: "", endTime: "",
        location: "", virtualMeetingLink: "",
        departmentId: "", participantMode: "all",
        participants: [], attachments: [],
        priority: "Medium", status: "Scheduled"
      };
      this.showMeetingForm = true;
    },

    editMeeting(meeting) {
      this.isEditMode = true;
      this.selectedMeeting = meeting;

      this.showMeetingDetails = false;

      // Calculate duration based on start and end time
      let duration = 60; // Default
      if (meeting.startTime && meeting.endTime) {
        const start = new Date(`2000-01-01T${meeting.startTime}`);
        const end = new Date(`2000-01-01T${meeting.endTime}`);
        duration = (end - start) / 60000; // Convert milliseconds to minutes
      }

      // Prepare form data
      this.meetingForm = {
        id: meeting.id,
        title: meeting.title,
        agenda: meeting.agenda || "",
        meetingType: meeting.meetingType,
        startDate: meeting.startDate,
        duration: duration,
        startTime: meeting.startTime || "",
        endTime: meeting.endTime || "",
        location: meeting.location || "",
        virtualMeetingLink: meeting.virtualMeetingLink || "",
        departmentId: meeting.departmentId || "",
        participantMode: meeting.departmentId ? "all" : "specific",
        participants: meeting.participantsList ? meeting.participantsList.map(att => att.employeeId) : [],
        attachments: [],
        priority: meeting.priority || "Medium",
        status: meeting.status || "Scheduled"
      };

      // Open the meeting modal
      this.$nextTick(() => {
        this.showMeetingForm = true;
      });
    },

    onMeetingAttachmentsChange(event) {
      const files = Array.from(event?.target?.files || []);
      const valid = files.filter((f) => /\.(pdf|doc|docx)$/i.test(String(f.name || "")));
      if (valid.length !== files.length) {
        this.showToast("error", "Only PDF, DOC, and DOCX files are allowed");
      }
      this.meetingForm.attachments = valid;
    },

    // Calculate end time when start time or duration changes
    updateEndTime() {
      if (this.meetingForm.startTime && this.meetingForm.duration) {
        const [hours, minutes] = this.meetingForm.startTime.split(':').map(Number);
        const startDate = new Date();
        startDate.setHours(hours, minutes, 0);

        const endDate = new Date(startDate.getTime() + this.meetingForm.duration * 60000);
        const endHours = endDate.getHours().toString().padStart(2, '0');
        const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

        this.meetingForm.endTime = `${endHours}:${endMinutes}`;
      } else {
        this.meetingForm.endTime = '';
      }
    },

    submitMeeting() {
      const missingFieldMessage = this.getMissingMeetingFieldMessage();
      if (missingFieldMessage) {
        this.showToast("error", missingFieldMessage);
        return;
      }

      // Update end time based on start time and duration
      this.updateEndTime();

      // Prepare form data
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateMeeting");
        data.append("meetingId", this.meetingForm.id);
      } else {
        data.append("function", "createMeeting");
      }

      // Add all form fields
      data.append("title", this.meetingForm.title);
      data.append("agenda", this.meetingForm.agenda);
      data.append("meetingType", this.meetingForm.meetingType);
      data.append("startDate", this.meetingForm.startDate);
      data.append("startTime", this.meetingForm.startTime);
      data.append("endTime", this.meetingForm.endTime);
      data.append("location", this.meetingForm.location);
      data.append("virtualMeetingLink", this.meetingForm.virtualMeetingLink);
      data.append("departmentId", this.meetingForm.departmentId);
      data.append("priority", this.meetingForm.priority);
      data.append("status", this.meetingForm.status);

      // Handle participants
      if (this.meetingForm.participantMode === "specific" && this.meetingForm.participants.length > 0) {
        // Create participants array of objects
        const participantsArray = this.meetingForm.participants.map(empId => {
          const employee = this.employees.find(emp => emp.id === empId);
          return {
            employeeId: empId,
            name: employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown'
          };
        });
        data.append("participants", JSON.stringify(participantsArray));
      }
      if (Array.isArray(this.meetingForm.attachments) && this.meetingForm.attachments.length) {
        this.meetingForm.attachments.forEach((f) => data.append("attachments[]", f));
      }

      // Send the request
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", this.isEditMode ? "Meeting updated successfully" : "Meeting created successfully");

              // Close the modal
              this.closeMeetingModal();

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error saving meeting:", res.data.message);
              this.showToast("error", `Failed to ${this.isEditMode ? 'update' : 'create'} meeting: ${res.data.message}`);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", `Network error when ${this.isEditMode ? 'updating' : 'creating'} meeting`);
          });
    },

    viewMeetingDetails(meeting) {
      this.selectedMeeting = null;
      this.getMeetingDetails(meeting.id);
    },

    cancelMeeting(meeting) {
      this.selectedMeeting = meeting;
      this.cancellationReason = "";
      this.notifyParticipants = true;
      this.showMeetingDetails = false;
      this.$nextTick(() => { this.showCancelForm = true; });
    },

    closeMeetingModal() {
      this.showMeetingForm = false;
    },

    closeMeetingDetailsModal() {
      this.showMeetingDetails = false;
    },

    confirmCancelMeeting() {
      if (!this.selectedMeeting) return;

      const data = new FormData();
      data.append("function", "cancelMeeting");
      data.append("meetingId", this.selectedMeeting.id);
      data.append("reason", this.cancellationReason);
      data.append("notifyParticipants", this.notifyParticipants ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting canceled successfully");

              this.showCancelForm = false;

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error canceling meeting:", res.data.message);
              this.showToast("error", "Failed to cancel meeting: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when canceling meeting");
          });
    },

    completeMeeting(meeting) {
      const data = new FormData();
      data.append("function", "completeMeeting");
      data.append("meetingId", meeting.id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting marked as completed");

              this.showMeetingDetails = false;

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error completing meeting:", res.data.message);
              this.showToast("error", "Failed to complete meeting: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when completing meeting");
          });
    },

    exportMeetings() {
      const data = new FormData();
      data.append("function", "exportMeetingsReport");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.meetingType) {
        data.append("meetingType", this.filters.meetingType);
      }

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    exportMeetingDetails(meeting) {
      if (!meeting) return;

      const data = new FormData();
      data.append("function", "exportMeetingDetails");
      data.append("meetingId", meeting.id);

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    addActionItem() {
      this.minutesForm.actionItems.push({
        description: "",
        assignedTo: "",
        dueDate: ""
      });
    },

    removeActionItem(index) {
      this.minutesForm.actionItems.splice(index, 1);
    },
    openMeetingMinutesModal(meeting) {
      this.selectedMeeting = meeting;

      // Reset the minutes form
      this.minutesForm = {
        content: "",
        actionItems: [],
        notifyParticipants: true
      };

      // Check if there are existing minutes to load them
      if (meeting.minutes) {
        this.minutesForm.content = meeting.minutes.content || "";
        this.minutesForm.actionItems = meeting.minutes.actionItems || [];
      }

      this.showMeetingDetails = false;
      this.$nextTick(() => {
        this.showMinutesForm = true;
      });
    },

    getMeetingDetails(meetingId) {
      const data = new FormData();
      data.append("function", "getMeetingDetails");
      data.append("meetingId", meetingId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedMeeting = res.data.data.selectedMeeting || res.data.data.meetingDetails;
              if (!this.selectedMeeting) {
                this.showToast("error", "Meeting not found");
                return;
              }

              // Process meeting notes if available (convert from JSON string if needed)
              if (this.selectedMeeting.meetingNotes) {
                try {
                  // If it's a JSON string, parse it
                  if (typeof this.selectedMeeting.meetingNotes === 'string') {
                    this.selectedMeeting.minutes = JSON.parse(this.selectedMeeting.meetingNotes);
                  } else {
                    // It's already an object
                    this.selectedMeeting.minutes = this.selectedMeeting.meetingNotes;
                  }

                  // Ensure proper structure - initialize with defaults if properties are missing
                  this.selectedMeeting.minutes = {
                    content: this.selectedMeeting.minutes.content || '',
                    createdAt: this.selectedMeeting.minutes.addedAt || this.selectedMeeting.updatedAt || new Date().toISOString(),
                    actionItems: this.selectedMeeting.minutes.actionItems || []
                  };

                  // Format action item dates if present
                  if (this.selectedMeeting.minutes.actionItems && this.selectedMeeting.minutes.actionItems.length > 0) {
                    this.selectedMeeting.minutes.actionItems.forEach(item => {
                      // Check for overdue items and update status
                      if (item.dueDate && new Date(item.dueDate) < new Date() && item.status !== 'Completed') {
                        item.status = 'Overdue';
                      }
                    });
                  }
                } catch (error) {
                  console.error("Error parsing meeting notes:", error);
                  // Initialize with an empty structure to prevent null reference errors
                  this.selectedMeeting.minutes = {
                    content: '',
                    createdAt: new Date().toISOString(),
                    actionItems: []
                  };
                }
              } else {
                // Initialize with an empty structure to prevent null reference errors
                this.selectedMeeting.minutes = {
                  content: '',
                  createdAt: new Date().toISOString(),
                  actionItems: []
                };
              }

              this.$nextTick(() => {
                this.showMeetingDetails = true;
              });
            } else {
              console.error("Error fetching meeting details:", res.data.message);
              this.showToast("error", "Failed to load meeting details: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching meeting details");
          });
    },


    submitMeetingMinutes() {
      // Validate required fields
      if (!this.minutesForm.content) {
        this.showToast("error", "Please enter meeting minutes content");
        return;
      }

      const data = new FormData();
      data.append("function", "saveMeetingMinutes");
      data.append("meetingId", this.selectedMeeting.id);
      data.append("content", this.minutesForm.content);
      data.append("notifyParticipants", this.minutesForm.notifyParticipants ? 1 : 0);

      // Create the meeting notes object in the expected format
      const meetingNotes = {
        content: this.minutesForm.content,
        actionItems: this.minutesForm.actionItems || [],
        addedBy: this.selectedMeeting.organizer || "USER", // Use meeting organizer or default
        addedAt: new Date().toISOString().replace('T', ' ').substring(0, 19) // Format: YYYY-MM-DD HH:MM:SS
      };

      data.append("meetingNotes", JSON.stringify(meetingNotes));

      if (this.minutesForm.actionItems.length > 0) {
        data.append("actionItems", JSON.stringify(this.minutesForm.actionItems));
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting minutes saved successfully");

              this.showMinutesForm = false;

              // Refresh meeting details
              this.getMeetingDetails(this.selectedMeeting.id);
            } else {
              console.error("Error saving meeting minutes:", res.data.message);
              this.showToast("error", "Failed to save meeting minutes: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when saving meeting minutes");
          });
    },

    // Helper methods
    viewMeetingMinutes(meeting) {
      this.selectedMeeting = meeting;

      // You could either show a dedicated modal for viewing minutes
      // or just scroll to the minutes section in the details modal
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (!detailsModal) {
        this.viewMeetingDetails(meeting);
      }
    },

    getActionItemStatusClass(status) {
      const statusMap = {
        'Completed': 'bg-success',
        'In Progress': 'bg-warning',
        'Pending': 'bg-secondary',
        'Overdue': 'bg-danger',
        'Not Started': 'bg-info'
      };

      return statusMap[status] || 'bg-secondary';
    },

    formatTime(timeString) {
      if (!timeString) return '';

      // If the time string is already in 12-hour format
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }

      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeString.split(':');
      let hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12;
      hour = hour ? hour : 12; // the hour '0' should be '12'
      return `${hour}:${minutes} ${ampm}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatMeetingTime(meeting) {
      if (!meeting.startTime) return 'No Time Specified';

      return `${this.formatTime(meeting.startTime)}${meeting.endTime ? ' - ' + this.formatTime(meeting.endTime) : ''}`;
    },

    getMeetingTypeColor(type) {
      if (!type) return 'bg-secondary';
      const typeMap = {
        'Team Meeting': 'bg-success', 'One-on-One': 'bg-info', 'Board Meeting': 'bg-primary',
        'Interview': 'bg-warning', 'Conference Call': 'bg-purple', 'Training': 'bg-indigo',
        'Workshop': 'bg-teal', 'Brainstorming': 'bg-pink', 'Review': 'bg-orange', 'Other': 'bg-secondary'
      };
      return typeMap[type] || 'bg-secondary';
    },
    getMeetingTypeClass(type) {
      if (!type) return 'mtg-type-other';
      const map = {
        'Team Meeting': 'mtg-type-team', 'One-on-One': 'mtg-type-one', 'Board Meeting': 'mtg-type-board',
        'Interview': 'mtg-type-interview', 'Conference Call': 'mtg-type-conf', 'Training': 'mtg-type-training',
        'Workshop': 'mtg-type-workshop', 'Brainstorming': 'mtg-type-brain', 'Review': 'mtg-type-review', 'Other': 'mtg-type-other'
      };
      return map[type] || 'mtg-type-other';
    },
    getStatusClass(status) {
      const map = { 'Scheduled': 'mtg-status-scheduled', 'Canceled': 'mtg-status-canceled', 'Completed': 'mtg-status-completed', 'In Progress': 'mtg-status-inprogress', 'Postponed': 'mtg-status-postponed' };
      return map[status] || 'mtg-status-scheduled';
    },
    getResponseClass(r) {
      const map = { 'Accepted': 'mtg-resp-accepted', 'Declined': 'mtg-resp-declined', 'Tentative': 'mtg-resp-tentative', 'Pending': 'mtg-resp-pending' };
      return map[r] || 'mtg-resp-pending';
    },
    getActionItemClass(s) {
      const map = { 'Completed': 'mtg-status-completed', 'Overdue': 'mtg-status-canceled', 'In Progress': 'mtg-status-inprogress', 'Pending': 'mtg-status-postponed' };
      return map[s] || 'mtg-status-postponed';
    },
    getInitials(name) {
      if (!name) return '?';
      return name.trim().split(/\s+/).slice(0,2).map(w => w[0].toUpperCase()).join('');
    },
    isMeetingToday(d) {
      if (!d) return false;
      const t = new Date(), h = new Date(d);
      return t.getFullYear()===h.getFullYear() && t.getMonth()===h.getMonth() && t.getDate()===h.getDate();
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Scheduled': 'bg-success',
        'Canceled': 'bg-danger',
        'Completed': 'bg-info',
        'In Progress': 'bg-warning',
        'Postponed': 'bg-warning'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getResponseBadgeClass(response) {
      if (!response) return 'bg-secondary';

      const responseMap = {
        'Accepted': 'bg-success',
        'Declined': 'bg-danger',
        'Tentative': 'bg-warning',
        'Pending': 'bg-secondary'
      };

      return responseMap[response] || 'bg-secondary';
    },

    getMeetingTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalMeetings) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      return ((count / this.metrics.totalMeetings) * 100).toFixed(1);
    },
    getEmployeeDepartmentId(employee) {
      return String(employee.departmentId || employee.department_id || employee.deptId || "");
    },
    getMissingMeetingFieldMessage() {
      if (!this.meetingForm.title || !String(this.meetingForm.title).trim()) {
        return "Please fill this: Meeting Title";
      }
      if (!this.meetingForm.meetingType) {
        return "Please fill this: Meeting Type";
      }
      if (!this.meetingForm.startDate) {
        return "Please fill this: Date";
      }
      if (!this.meetingForm.duration || Number(this.meetingForm.duration) < 15) {
        return "Please fill this: Duration (minimum 15 minutes)";
      }
      if (!this.meetingForm.startTime) {
        return "Please fill this: Start Time";
      }
      if (!this.meetingForm.departmentId && this.meetingForm.participantMode === "specific" && (!Array.isArray(this.meetingForm.participants) || this.meetingForm.participants.length === 0)) {
        return "Please fill this: Specific Participants";
      }
      return "";
    },

    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to alert if toast plugin not available
        alert(message);
      }
    }
  },
  watch: {
    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.meetingType': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.startDate': _.debounce(function() {
      if (!this.filters.startDate) {
        this.filters.startDate = this.getDateOffset(-30);
      }
      this.filters.page = 1;
      this.getMeetings();
    }, 300),

    'filters.endDate': _.debounce(function() {
      if (!this.filters.endDate) {
        this.filters.endDate = this.getDateOffset(30);
      }
      this.filters.page = 1;
      this.getMeetings();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 500),

    'meetingForm.startTime': function() {
      this.updateEndTime();
    },

    'meetingForm.duration': function() {
      this.updateEndTime();
    },
    'meetingForm.departmentId': function(newDepartmentId) {
      if (newDepartmentId) {
        this.meetingForm.participantMode = 'all';
        this.meetingForm.participants = [];
        this.participantSearch = '';
      }
    },
    'meetingForm.participantMode': function(newMode) {
      if (newMode !== 'specific') {
        this.meetingForm.participants = [];
        this.participantSearch = '';
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.mtg-page{display:flex;flex-direction:column;gap:20px}
.mtg-toolbar{display:flex;align-items:center;justify-content:space-between;background:#fff;border-radius:14px;border:1px solid #e2e8f0;padding:8px 12px;box-shadow:0 2px 8px rgba(0,0,0,.05);flex-wrap:wrap;gap:10px}
.mtg-tabs{display:flex;gap:4px;background:#f1f5f9;border-radius:10px;padding:4px}
.mtg-tab{display:flex;align-items:center;gap:7px;padding:8px 16px;border-radius:8px;border:none;background:transparent;color:#64748b;font-size:13px;font-weight:600;cursor:pointer;transition:all .18s;white-space:nowrap}
.mtg-tab i{font-size:16px}
.mtg-tab:hover{background:#e2e8f0;color:#1e293b}
.mtg-tab-active{background:#fff!important;color:#059669!important;box-shadow:0 2px 8px rgba(0,0,0,.08)}
.mtg-tab-count{background:#059669;color:#fff;font-size:10px;font-weight:700;padding:1px 6px;border-radius:20px}
.mtg-tab-count-green{background:#059669}
.mtg-toolbar-right{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.mtg-btn{display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;border:1px solid transparent;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap}
.mtg-btn i{font-size:15px}
.mtg-btn-sm{padding:6px 12px;font-size:12px}
.mtg-btn-ghost{background:#f8fafc;color:#475569;border-color:#e2e8f0}
.mtg-btn-ghost:hover{background:#e2e8f0}
.mtg-btn-primary{background:linear-gradient(135deg,#059669,#0d9488);color:#fff;box-shadow:0 2px 8px rgba(5,150,105,.3)}
.mtg-btn-primary:hover:not(:disabled){background:linear-gradient(135deg,#047857,#0f766e);transform:translateY(-1px)}
.mtg-btn-primary:disabled{opacity:.6;cursor:not-allowed}
.mtg-btn-danger{background:#ef4444;color:#fff}
.mtg-btn-danger:hover{background:#dc2626}
.mtg-btn-outline-info{background:#f0f9ff;color:#0ea5e9;border-color:#bae6fd}
.mtg-btn-outline-info:hover{background:#0ea5e9;color:#fff}
.mtg-btn-outline-success{background:#f0fdf4;color:#059669;border-color:#bbf7d0}
.mtg-btn-outline-success:hover{background:#059669;color:#fff}
.mtg-btn-spinner{width:13px;height:13px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:mtg-spin .7s linear infinite;display:inline-block}
@keyframes mtg-spin{to{transform:rotate(360deg)}}
.mtg-summary-row{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.mtg-summary-card{background:#fff;border-radius:14px;border:1px solid #e2e8f0;padding:18px 20px;display:flex;align-items:center;gap:14px;box-shadow:0 2px 8px rgba(0,0,0,.04);position:relative;overflow:hidden}
.mtg-summary-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:var(--c,#059669)}
.mtg-summary-icon{width:46px;height:46px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
.mtg-summary-label{display:block;font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px}
.mtg-summary-value{display:block;font-size:28px;font-weight:800;color:#1e293b;line-height:1}
.mtg-summary-sub{display:block;font-size:11px;color:#94a3b8;margin-top:3px}
.mtg-card{background:#fff;border-radius:14px;border:1px solid #e2e8f0;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.04)}
.mtg-card-header{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#f8fafc;border-bottom:1px solid #e2e8f0}
.mtg-card-title{display:flex;align-items:center;gap:8px;font-size:14px;font-weight:700;color:#1e293b}
.mtg-card-title i{font-size:18px;color:#059669}
.mtg-row-count{font-size:12px;color:#94a3b8;font-weight:600;background:#f1f5f9;padding:3px 10px;border-radius:20px}
.mtg-filters-card .mtg-filters-row{display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end;padding:16px 20px}
.mtg-filter-group{display:flex;flex-direction:column;gap:4px;min-width:130px}
.mtg-filter-group label{font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.4px}
.mtg-filter-search{flex:1;min-width:180px}
.mtg-filter-reset{align-self:flex-end}
.mtg-select{padding:7px 10px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:13px;color:#1e293b;background:#fff;outline:none;cursor:pointer}
.mtg-select:focus{border-color:#059669}
.mtg-input{width:100%;padding:7px 10px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:13px;color:#1e293b;background:#fff;outline:none;box-sizing:border-box}
.mtg-input:focus{border-color:#059669}
.mtg-search-wrap{position:relative}
.mtg-search-wrap i{position:absolute;left:9px;top:50%;transform:translateY(-50%);font-size:15px;color:#94a3b8;pointer-events:none}
.mtg-search-wrap .mtg-input{padding-left:30px}
.mtg-main-layout{display:grid;grid-template-columns:1fr 280px;gap:20px;align-items:start}
.mtg-list-card{flex:1}
.mtg-sidebar{display:flex;flex-direction:column;gap:16px}
.mtg-loading{display:flex;align-items:center;justify-content:center;gap:12px;padding:60px 20px;color:#64748b;font-size:14px}
.mtg-spinner{width:22px;height:22px;border:3px solid #e2e8f0;border-top-color:#059669;border-radius:50%;animation:mtg-spin .8s linear infinite}
.mtg-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;color:#94a3b8;text-align:center}
.mtg-empty i{font-size:52px;color:#059669;margin-bottom:12px}
.mtg-empty h5{font-size:16px;font-weight:700;color:#1e293b;margin:0 0 6px}
.mtg-empty p{font-size:13px;margin:0 0 16px}
.mtg-empty-sm{display:flex;flex-direction:column;align-items:center;padding:30px 16px;color:#94a3b8;text-align:center;gap:8px}
.mtg-empty-sm i{font-size:32px}
.mtg-empty-sm p{margin:0;font-size:13px}
.mtg-empty-text{font-size:12px;color:#94a3b8;margin:0}
.mtg-table-wrap{overflow-x:auto}
.mtg-table{width:100%;border-collapse:collapse;font-size:13px}
.mtg-table th{background:#f8fafc;padding:10px 14px;text-align:left;font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #e2e8f0;white-space:nowrap}
.mtg-table td{padding:12px 14px;border-bottom:1px solid #f1f5f9;color:#334155;vertical-align:middle}
.mtg-table tr:last-child td{border-bottom:none}
.mtg-table tr:hover td{background:#f8fafc}
.mtg-row-today td{background:#f0fdf4!important}
.mtg-td-num{color:#94a3b8;font-size:12px;font-weight:600;width:40px}
.mtg-meeting-cell{display:flex;align-items:center;gap:10px}
.mtg-meeting-avatar{width:36px;height:36px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:16px;color:#fff;flex-shrink:0}
.mtg-meeting-title{display:block;font-weight:700;color:#1e293b}
.mtg-meeting-code{display:block;font-size:11px;color:#94a3b8;margin-top:1px}
.mtg-date{display:block;font-weight:600;color:#1e293b}
.mtg-time{display:flex;align-items:center;gap:4px;font-size:11px;color:#94a3b8;margin-top:2px}
.mtg-location{display:flex;align-items:center;gap:4px;font-size:12px;color:#475569}
.mtg-virtual{display:flex;align-items:center;gap:4px;font-size:12px;color:#0ea5e9}
.mtg-na{color:#cbd5e1;font-size:12px}
.mtg-type-pill{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700}
.mtg-type-pill-sm{font-size:10px;padding:2px 7px}
.mtg-type-team{background:#f0fdf4;color:#059669}
.mtg-type-one{background:#f0f9ff;color:#0ea5e9}
.mtg-type-board{background:#eff6ff;color:#2563eb}
.mtg-type-interview{background:#fffbeb;color:#d97706}
.mtg-type-conf{background:#fdf4ff;color:#9333ea}
.mtg-type-training{background:#fff7ed;color:#ea580c}
.mtg-type-workshop{background:#f0fdfa;color:#0d9488}
.mtg-type-brain{background:#fdf2f8;color:#db2777}
.mtg-type-review{background:#fff1f2;color:#e11d48}
.mtg-type-other{background:#f1f5f9;color:#64748b}
.mtg-status-pill{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700}
.mtg-status-scheduled{background:#f0fdf4;color:#059669}
.mtg-status-canceled{background:#fef2f2;color:#ef4444}
.mtg-status-completed{background:#eff6ff;color:#2563eb}
.mtg-status-inprogress{background:#fffbeb;color:#d97706}
.mtg-status-postponed{background:#fdf4ff;color:#9333ea}
.mtg-priority-pill{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700}
.mtg-priority-low{background:#f0fdf4;color:#059669}
.mtg-priority-medium{background:#fffbeb;color:#d97706}
.mtg-priority-high{background:#fef2f2;color:#ef4444}
.mtg-row-actions{display:flex;gap:6px}
.mtg-action-btn{width:30px;height:30px;border-radius:7px;border:none;display:flex;align-items:center;justify-content:center;font-size:15px;cursor:pointer;transition:all .15s}
.mtg-action-view{background:#eff6ff;color:#2563eb}
.mtg-action-view:hover{background:#2563eb;color:#fff}
.mtg-action-edit{background:#f0fdf4;color:#059669}
.mtg-action-edit:hover{background:#059669;color:#fff}
.mtg-action-cancel{background:#fef2f2;color:#ef4444}
.mtg-action-cancel:hover{background:#ef4444;color:#fff}
.mtg-pagination{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-top:1px solid #f1f5f9}
.mtg-pagination-info{font-size:12px;color:#94a3b8}
.mtg-pages{display:flex;gap:4px}
.mtg-page-btn{width:32px;height:32px;border-radius:7px;border:1px solid #e2e8f0;background:#fff;color:#475569;font-size:13px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
.mtg-page-btn:hover:not(:disabled){background:#f1f5f9}
.mtg-page-btn:disabled{opacity:.4;cursor:not-allowed}
.mtg-page-active{background:#059669!important;color:#fff!important;border-color:#059669!important}
.mtg-upcoming-list{display:flex;flex-direction:column}
.mtg-upcoming-item{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid #f1f5f9;cursor:pointer;transition:background .15s}
.mtg-upcoming-item:last-child{border-bottom:none}
.mtg-upcoming-item:hover{background:#f8fafc}
.mtg-upcoming-date{width:44px;text-align:center;flex-shrink:0}
.mtg-upcoming-month{display:block;font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase}
.mtg-upcoming-day{display:block;font-size:22px;font-weight:800;color:#1e293b;line-height:1}
.mtg-upcoming-info{flex:1;min-width:0}
.mtg-upcoming-title{display:block;font-size:13px;font-weight:700;color:#1e293b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mtg-upcoming-meta{display:flex;align-items:center;gap:4px;font-size:11px;color:#94a3b8;margin-top:2px}
.mtg-type-breakdown{padding:14px 16px;display:flex;flex-direction:column;gap:12px}
.mtg-type-row{display:flex;flex-direction:column;gap:4px}
.mtg-type-row-header{display:flex;justify-content:space-between;align-items:center}
.mtg-type-name{font-size:12px;font-weight:600;color:#1e293b}
.mtg-type-count{font-size:12px;font-weight:700;color:#475569}
.mtg-type-bar-wrap{height:5px;background:#f1f5f9;border-radius:3px;overflow:hidden}
.mtg-type-bar{height:100%;border-radius:3px;transition:width .4s}
.mtg-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:1040}
.mtg-panel{position:fixed;top:0;right:-520px;width:500px;height:100vh;background:#fff;z-index:1050;display:flex;flex-direction:column;transition:right .3s cubic-bezier(.4,0,.2,1);box-shadow:-8px 0 40px rgba(0,0,0,.15)}
.mtg-panel-sm{width:420px;right:-440px}
.mtg-panel-open{right:0!important}
.mtg-panel-header{background:linear-gradient(135deg,#059669,#0d9488);padding:20px 24px;display:flex;align-items:center;justify-content:space-between;gap:14px;flex-shrink:0}
.mtg-panel-title-wrap{display:flex;align-items:center;gap:14px}
.mtg-panel-icon{width:46px;height:46px;border-radius:12px;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:22px;color:#fff;flex-shrink:0}
.mtg-panel-title{margin:0;font-size:17px;font-weight:700;color:#fff}
.mtg-panel-subtitle{margin:3px 0 0;font-size:12px;color:rgba(255,255,255,.75)}
.mtg-panel-close{background:rgba(255,255,255,.15);border:none;color:#fff;width:34px;height:34px;border-radius:8px;font-size:20px;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:background .15s}
.mtg-panel-close:hover{background:rgba(255,255,255,.3)}
.mtg-panel-body{flex:1;overflow-y:auto;padding:0}
.mtg-panel-footer{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#fff;border-top:1px solid #e2e8f0;flex-shrink:0;gap:10px}
.mtg-form-section{border-bottom:1px solid #f1f5f9}
.mtg-form-section:last-child{border-bottom:none}
.mtg-form-section-label{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:12px 20px;background:#f8fafc;border-bottom:1px solid #e2e8f0;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.5px}
.mtg-form-section-label i{font-size:15px;color:#059669}
.mtg-form-section-label .mtg-req{color:#ef4444;font-size:13px}
.mtg-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;padding:16px 20px}
.mtg-field{display:flex;flex-direction:column;gap:5px}
.mtg-field-full{grid-column:span 2}
.mtg-label{font-size:12px;font-weight:700;color:#374151}
.mtg-req{color:#ef4444}
.mtg-input-icon-wrap{position:relative}
.mtg-input-icon{position:absolute;left:9px;top:50%;transform:translateY(-50%);font-size:15px;color:#94a3b8;pointer-events:none}
.mtg-input-icon-pad{padding-left:30px}
.mtg-textarea{width:100%;padding:8px 12px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:13px;color:#1e293b;background:#fff;outline:none;resize:vertical;box-sizing:border-box}
.mtg-textarea:focus{border-color:#059669}
.mtg-priority-group{display:flex;gap:8px}
.mtg-priority-opt{flex:1;display:flex;align-items:center;justify-content:center;gap:5px;padding:8px 10px;border-radius:8px;border:1.5px solid #e2e8f0;font-size:12px;font-weight:600;cursor:pointer;transition:all .15s;color:#64748b;background:#f8fafc}
.mtg-priority-low{background:#f0fdf4!important;border-color:#059669!important;color:#059669!important}
.mtg-priority-medium{background:#fffbeb!important;border-color:#f59e0b!important;color:#d97706!important}
.mtg-priority-high{background:#fef2f2!important;border-color:#ef4444!important;color:#ef4444!important}
.mtg-scope-info{display:flex;align-items:center;gap:8px;padding:10px 14px;background:#f0fdf4;border-radius:8px;font-size:13px;color:#059669;font-weight:600}
.mtg-mode-group{display:flex;gap:10px}
.mtg-mode-opt{flex:1;display:flex;align-items:center;justify-content:center;gap:7px;padding:10px 14px;border-radius:8px;border:1.5px solid #e2e8f0;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s;color:#64748b;background:#f8fafc}
.mtg-mode-active{border-color:#059669!important;background:#f0fdf4!important;color:#059669!important}
.mtg-file-drop{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;padding:24px 20px;border:2px dashed #cbd5e1;border-radius:10px;cursor:pointer;background:#f8fafc;transition:all .15s}
.mtg-file-drop:hover{border-color:#059669;background:#f0fdf4}
.mtg-file-icon{font-size:30px;color:#94a3b8}
.mtg-file-drop:hover .mtg-file-icon{color:#059669}
.mtg-file-hint{font-size:11px;color:#94a3b8}
.mtg-detail-badges{display:flex;flex-wrap:wrap;gap:8px;padding:16px 20px;border-bottom:1px solid #f1f5f9}
.mtg-detail-meta{display:flex;flex-wrap:wrap;background:#f8fafc;border-bottom:1px solid #e2e8f0}
.mtg-detail-meta-item{display:flex;align-items:center;gap:10px;padding:14px 20px;border-right:1px solid #e2e8f0;flex:1;min-width:120px}
.mtg-detail-meta-item i{font-size:20px;color:#059669;flex-shrink:0}
.mtg-meta-label{display:block;font-size:11px;color:#94a3b8;font-weight:600;text-transform:uppercase;letter-spacing:.4px}
.mtg-meta-value{display:block;font-size:13px;color:#1e293b;font-weight:600;margin-top:1px}
.mtg-detail-text{font-size:14px;color:#475569;line-height:1.7;margin:0;padding:14px 20px}
.mtg-join-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 20px;background:#0ea5e9;color:#fff;border-radius:8px;text-decoration:none;font-size:13px;font-weight:600;transition:background .15s}
.mtg-join-btn:hover{background:#0284c7;color:#fff}
.mtg-count-badge{background:#2563eb;color:#fff;padding:2px 8px;border-radius:20px;font-size:11px;font-weight:700}
.mtg-participants-list{display:flex;flex-direction:column;padding:14px 20px;gap:10px}
.mtg-participant-item{display:flex;align-items:center;gap:12px}
.mtg-participant-avatar{width:36px;height:36px;border-radius:50%;background:linear-gradient(135deg,#059669,#0d9488);color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.mtg-participant-info{flex:1;min-width:0}
.mtg-participant-name{display:block;font-size:13px;font-weight:700;color:#1e293b}
.mtg-participant-dept{display:block;font-size:11px;color:#94a3b8}
.mtg-resp-accepted{background:#f0fdf4;color:#059669}
.mtg-resp-declined{background:#fef2f2;color:#ef4444}
.mtg-resp-tentative{background:#fffbeb;color:#d97706}
.mtg-resp-pending{background:#f1f5f9;color:#64748b}
.mtg-toggle-item{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-radius:10px;border:1.5px solid #e2e8f0;cursor:pointer;transition:all .15s;background:#f8fafc}
.mtg-toggle-item:hover{border-color:#059669}
.mtg-toggle-on{border-color:#059669!important;background:#f0fdf4!important}
.mtg-toggle-info{display:flex;align-items:center;gap:10px}
.mtg-toggle-info i{font-size:20px;color:#94a3b8}
.mtg-toggle-on .mtg-toggle-info i{color:#059669}
.mtg-toggle-title{display:block;font-size:13px;font-weight:700;color:#1e293b}
.mtg-toggle-desc{display:block;font-size:11px;color:#94a3b8}
.mtg-switch{width:42px;height:22px;border-radius:11px;background:#e2e8f0;position:relative;transition:background .2s;flex-shrink:0}
.mtg-toggle-on .mtg-switch{background:#059669}
.mtg-switch-knob{position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;transition:transform .2s;box-shadow:0 1px 4px rgba(0,0,0,.2)}
.mtg-toggle-on .mtg-switch-knob{transform:translateX(20px)}
.mtg-action-item-row{display:flex;gap:8px;align-items:center}
.mtg-action-item-row .mtg-input{flex:1}
@media(max-width:1100px){.mtg-main-layout{grid-template-columns:1fr}.mtg-summary-row{grid-template-columns:1fr 1fr}}
@media(max-width:600px){.mtg-summary-row{grid-template-columns:1fr}.mtg-toolbar{flex-direction:column;align-items:stretch}.mtg-panel{width:100%;right:-100%}.mtg-panel-sm{width:100%;right:-100%}.mtg-form-grid{grid-template-columns:1fr}.mtg-field-full{grid-column:span 1}}
</style>

<style src="@/assets/css/hr-page-shared.css"></style>